import React, { useState, useEffect } from "react";
import { FiCopy, FiTrash2, FiArrowRight } from "react-icons/fi";
import { MdOutlineModeEdit } from "react-icons/md";
import CustomSelect from '../../../customComponents/CustomSelect';
import MovementModal from '../../../customComponents/MovementModal';
import styles from "./MovementSidePanel.module.css";
import { API_URL } from '../../../config/api';


const MovementSidePanel = ({ id, from: initialFrom, to: initialTo, onClose, onSuccess  }) => {
  const token = localStorage.getItem('token'); 
  const [type, setType] = useState('');

  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(initialTo);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState([]);
  const [storages, setStorages] = useState([]);
  const [products, setProducts] = useState([]);

  // ✅ Загружаем склады
  useEffect(() => {
    const fetchStorages = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/api/storages`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Ошибка ${res.status}: ${text}`);
        }
        const json = await res.json();
        setStorages(json.data || []);
        localStorage.setItem('storages', JSON.stringify(json.data || []));
      } catch (err) {
        console.error("Ошибка загрузки складов:", err);
        const saved = localStorage.getItem('storages');
        if (saved) setStorages(JSON.parse(saved));
      }
    };
    fetchStorages();
  }, [token]);

  // ✅ Загружаем продукты
  useEffect(() => {
    const fetchProducts = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/api/products`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Ошибка ${res.status}: ${text}`);
        }
        const json = await res.json();
        setProducts(json.data || []);
        localStorage.setItem('products', JSON.stringify(json.data || []));
      } catch (err) {
        console.error("Ошибка загрузки продуктов:", err);
        const saved = localStorage.getItem('products');
        if (saved) setProducts(JSON.parse(saved));
      }
    };
    fetchProducts();
  }, [token]);

  // ✅ Загружаем товары перемещения
  useEffect(() => {
    const fetchItems = async () => {
      if (!id || !token) return;
      try {
        const res = await fetch(`${API_URL}/api/stock_changes/${id}/items`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Ошибка ${res.status}: ${text}`);
        }
        const json = await res.json();
        const items = json.data.map((item) => {
          const product = products.find((p) => p.id === item.product_id);
          return {
            id: item.product_id,
            name: product ? product.name_ru : `ID ${item.product_id}`,
            barcode: product ? product.ntin : "-",
            stock: item.ending_quantity,
            quantity: item.quantity,
            unit: "шт",
          };
        });
        setData(items);
      } catch (err) {
        console.error("Ошибка загрузки товаров перемещения:", err);
      }
    };
    fetchItems();
  }, [id, products, token]);

  const getStorageName = (id) => {
    const storage = storages.find((s) => s.id === Number(id));
    return storage ? storage.name : id;
  };

  const handleCopy = (barcode) => {
    navigator.clipboard.writeText(barcode);
    alert(`Скопировано: ${barcode}`);
  };

  const changeQuantity = (id, value) => {
    setData((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const newQty = item.quantity + value;
          if (newQty < 1) return { ...item, quantity: 1 };
          if (newQty > item.stock) return { ...item, quantity: item.stock };
          return { ...item, quantity: newQty };
        }
        return item;
      })
    );
  };

  const handleDelete = (id) => {
    setData((prev) => prev.filter((item) => item.id !== id));
  };

  const handleAddItem = (item) => {
    setData((prev) => {
      if (prev.some((el) => el.id === item.product_id)) return prev;
      return [
        ...prev,
        {
          id: item.product_id,
          name: item.name_ru,
          barcode: item.ntin,
          stock: item.quantity,
          quantity: 1,
          unit: "шт",
        },
      ];
    });
    setQuery("");
    setResults([]);
  };

  const handleUpdateMovement = ({ from, to, comment }) => {
    setFrom(from);
    setTo(to);
    setIsModalOpen(false);
  };

  // ✅ Отправка перемещения
  const handleFinish = async () => {
    if (!from || !to || data.length === 0) {
      alert("Выберите склады и добавьте хотя бы один товар");
      return;
    }

    const payload = {
      storage_id: Number(to),
      source_storage_id: Number(from),
      type: "transfer",
      quantity: data.reduce((sum, item) => sum + item.quantity, 0),
      status: "for_approval",
      items: data.map((item) => ({
        product_id: item.id,
        quantity: item.quantity,
      })),
    };

    try {
      const res = await fetch(`${API_URL}/api/stock_changes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Ошибка ${res.status}: ${text}`);
      }

      const result = await res.json();
      console.log("Успешно отправлено:", result);

      setData([]);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Ошибка отправки:", err);
      alert(`Ошибка при отправке: ${err.message}`);
    }
  };

  // ✅ Поиск продуктов
  useEffect(() => {
    const delayDebounce = setTimeout(async () => {
      if (query.length > 2 && from && token) {
        setLoading(true);
        try {
          const res = await fetch(`${API_URL}/api/stocks?storage_id=${from}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!res.ok) {
            const text = await res.text();
            throw new Error(`Ошибка ${res.status}: ${text}`);
          }
          const data = await res.json();
          const filtered = data.data.filter((item) =>
            item.name_ru.toLowerCase().includes(query.toLowerCase())
          );
          setResults(filtered);
        } catch (err) {
          console.error("Ошибка поиска:", err);
        } finally {
          setLoading(false);
        }
      } else {
        setResults([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [query, from, token]);


  return (
    <div className={styles.overlay}>
      <div className={styles.panel}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={onClose}>
            <p className={styles.backText}>Перемещение позиций</p>
          </button>
        </div>

        <div className={styles.searchWrapper}>
          <div className={styles.search}>
            <input
              type="search"
              placeholder="Поиск товара..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className={styles.input}
            />
          </div>

          <div className={styles.results}>
            {loading && <p className={styles.loading}>Загрузка...</p>}
            {!loading && results.length > 0 && (
              <ul className={styles.list}>
                {results.map((item) => (
                  <li
                    key={item.product_id}
                    className={styles.item}
                    onClick={() => handleAddItem(item)}
                  >
                    <div className={styles.itemName}>{item.name_ru}</div>
                    <div className={styles.itemQty}>
                      Остаток: {item.quantity}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {!loading && query.length > 3 && results.length === 0 && (
              <p className={styles.noResults}>Ничего не найдено</p>
            )}
          </div>
        </div>

        {data.length > 0 && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>№</th>
                <th>Название</th>
                <th>Штрихкод</th>
                <th>Остаток</th>
                <th>Количество</th>
                <th>Ед. изм.</th>
                <th>Настройки</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, idx) => (
                <tr key={item.id}>
                  <td>{idx + 1}</td>
                  <td>{item.name}</td>
                  <td>
                    <FiCopy
                      className={styles.copyIcon}
                      onClick={() => handleCopy(item.barcode)}
                    />
                    {item.barcode}
                  </td>
                  <td>{item.stock}</td>
                  <td>
                    <button
                      onClick={() => changeQuantity(item.id, -1)}
                      className={styles.qtyBtn}
                    >
                      -
                    </button>
                    <span className={styles.qty}>{item.quantity}</span>
                    <button
                      onClick={() => changeQuantity(item.id, 1)}
                      className={styles.qtyBtn}
                    >
                      +
                    </button>
                  </td>
                  <td>{item.unit}</td>
                  <td>
                    <button
                      className={styles.deleteBtn}
                      onClick={() => handleDelete(item.id)}
                    >
                      <FiTrash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            <span className={styles.from}>{getStorageName(from)}</span>
            <FiArrowRight className={styles.arrow} />
            <span className={styles.to}>{getStorageName(to)}</span>
            <button 
              className={styles.editBtn} 
              onClick={() => setIsModalOpen(true)}
            >
              <MdOutlineModeEdit className={styles.edit}/>
            </button>
          </div>

          <div className={styles.footerRight}>
            <CustomSelect
              className={styles.filters__select}
              options={['Товар однотипный', 'Товар многотипный', 'Услуга', 'Составной товар']}
              value={type}
              onChange={setType}
              placeholder="Товар однотипный"
            />
            <input
              type="text"
              className={styles.comment}
              placeholder="Комментарий"
            />
            <button className={styles.finishBtn} onClick={handleFinish}>Завершить</button>
          </div>
        </div>
      </div>
      {isModalOpen && (
        <MovementModal
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleUpdateMovement}
        />
      )}
    </div>
    
  );
};

export default MovementSidePanel;
